import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import TestimonialSlider from '@/components/TestimonialSlider';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Search, CheckCircle, XCircle, AlertTriangle, FileText } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function TrademarkSearch() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    {
      icon: Search,
      title: 'Comprehensive Search',
      description: 'Thorough analysis of federal and state trademark databases.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
    {
      icon: CheckCircle,
      title: 'Conflict Detection',
      description: 'Identify potential conflicts before you file your application.',
      image: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
    {
      icon: FileText,
      title: 'Detailed Report',
      description: 'Receive a comprehensive report with all search findings.',
      image: 'https://images.unsplash.com/photo-1554224311-beee460201f9?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
    {
      icon: AlertTriangle,
      title: 'Risk Assessment',
      description: 'Professional analysis of potential registration risks.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
  ];

  const searchTypes = [
    {
      type: 'Basic Search',
      description: 'Quick search of exact matches in federal database',
      features: ['Federal database search', 'Exact name matches', 'Same class identification', 'Basic risk assessment'],
      price: '$99',
    },
    {
      type: 'Comprehensive Search',
      description: 'Thorough search including similar marks and phonetic matches',
      features: ['Federal and state databases', 'Similar mark identification', 'Phonetic matching', 'Common law searches', 'Detailed analysis report'],
      price: '$199',
      recommended: true,
    },
    {
      type: 'Full Search',
      description: 'Complete search with international databases and extensive analysis',
      features: ['All US databases', 'International database search', 'Domain name analysis', 'Social media check', 'Business registry search', 'Comprehensive legal opinion'],
      price: '$299',
    },
  ];

  const faqs = [
    {
      question: 'Why is a trademark search important?',
      answer: 'A trademark search helps identify existing trademarks that may conflict with your proposed mark. This reduces the risk of rejection by the USPTO and potential legal disputes with other trademark owners.',
    },
    {
      question: 'What databases are searched?',
      answer: 'We search the USPTO federal trademark database, state trademark databases, common law sources, domain registrations, and business name registries to provide comprehensive coverage.',
    },
    {
      question: 'How long does a trademark search take?',
      answer: 'Basic searches are typically completed within 1-2 business days. Comprehensive and full searches may take 3-5 business days to ensure thorough analysis and reporting.',
    },
    {
      question: 'What if conflicts are found?',
      answer: 'If conflicts are identified, we provide detailed analysis and recommendations. This may include suggestions for modifying your mark, choosing different classes, or pursuing alternative strategies.',
    },
    {
      question: 'Do I need a search if I already know my mark is unique?',
      answer: 'Yes, even if you believe your mark is unique, a professional search can uncover similar marks you might have missed. It provides legal protection and peace of mind before investing in your brand.',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="trademark-search-page">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden" data-testid="service-hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/90 via-[#06214c]/85 to-[#A0522D]/80"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="service-hero-title"
            >
              Trademark Search
            </h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8" data-testid="service-hero-subtitle">
              Conduct a comprehensive trademark search to identify potential conflicts before filing. Make informed decisions about your brand protection strategy.
            </p>
            <button
              onClick={() => setShowDialog(true)}
              className="btn-primary"
              data-testid="service-hero-cta"
            >
              Start Your Search
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section section-abstract-bg" data-testid="service-benefits-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Why Conduct a Trademark Search?
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Protect your investment by identifying potential conflicts before filing your trademark application.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="group relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 fade-in"
                  data-testid={`benefit-${index}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={benefit.image} 
                      alt={benefit.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2C1810]/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                        <Icon className="text-[#06214c]" size={24} />
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Search Types Section */}
      <section className="section bg-[#FFF5E6]" data-testid="search-types-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Choose Your Search Package
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Select the right level of trademark search for your needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {searchTypes.map((search, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl shadow-lg p-8 ${
                  search.recommended ? 'ring-4 ring-[#06214c] transform scale-105' : ''
                }`}
                data-testid={`search-type-${index}`}
              >
                {search.recommended && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#06214c] text-white px-6 py-2 rounded-full text-sm font-semibold">
                    Recommended
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-[#06214c] mb-2" style={{ fontFamily: 'Playfair Display' }}>
                    {search.type}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">{search.description}</p>
                  <div className="text-4xl font-bold text-[#2C1810]">{search.price}</div>
                </div>
                <ul className="space-y-3 mb-6">
                  {search.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start space-x-2">
                      <CheckCircle className="text-[#06214c] flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setShowDialog(true)}
                  className={`w-full ${search.recommended ? 'btn-primary' : 'btn-secondary'}`}
                  data-testid={`select-search-${index}`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section" data-testid="search-process-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Our Search Process
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              A systematic approach to ensure comprehensive trademark analysis.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              { 
                step: 1, 
                title: 'Submit Your Search Request', 
                description: 'Provide your proposed trademark and relevant details about your business and intended use.' 
              },
              { 
                step: 2, 
                title: 'Database Analysis', 
                description: 'Our experts search federal, state, and common law databases for potential conflicts.' 
              },
              { 
                step: 3, 
                title: 'Risk Assessment', 
                description: 'We analyze findings and assess the likelihood of registration success and potential conflicts.' 
              },
              { 
                step: 4, 
                title: 'Detailed Report', 
                description: 'Receive a comprehensive report with findings, risk analysis, and strategic recommendations.' 
              },
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-6 bg-white rounded-xl p-6 shadow-lg" data-testid={`process-step-${index}`}>
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center text-white font-bold text-xl">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#06214c] mb-2" style={{ fontFamily: 'Playfair Display' }}>
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section section-abstract-shapes bg-[#FFF5E6]" data-testid="service-faq-section">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Common questions about trademark searches.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger
                    className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]"
                    data-testid={`faq-question-${index}`}
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]" data-testid={`faq-answer-${index}`}>
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Testimonials Slider */}
      <TestimonialSlider autoSlide={true} />

      {/* CTA Section */}
      <section 
        className="section relative overflow-hidden" 
        data-testid="service-cta-section"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1638262052640-82e94d64664a?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#06214c]/95 to-[#A0522D]/95"></div>
        </div>
        <div className="container mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white" style={{ fontFamily: 'Playfair Display' }}>
            Ready to Search Your Trademark?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-white/90">
            Start with a comprehensive trademark search to protect your brand investment.
          </p>
          <button
            onClick={() => setShowDialog(true)}
            className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl"
            data-testid="service-cta-btn"
          >
            Start Your Search
          </button>
        </div>
      </section>

      <Footer />
      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="trademark_search" />
    </div>
  );
}
